import { useRef, type ReactNode, type RefObject } from 'react'

import clsx from 'clsx'

type ScreenSectionProps = {
  children: ReactNode
  className?: string
}

type ContentProps = ScreenSectionProps & {
  scrollBodyRef?: RefObject<HTMLDivElement | null>
}

type ContextProps = ScreenSectionProps & {
  /** Remove padding e max-w para heroes full-bleed (ex: /objetivos). */
  fullBleed?: boolean
}

export const CONTAINER_PADDING = 'px-3'
const CONTENT_MAX_W = 'mx-auto w-full max-w-2xl'

function Root({ children, className }: ScreenSectionProps) {
  return (
    <div className={clsx('flex min-h-0 flex-1 flex-col', className)}>
      {children}
    </div>
  )
}

/** Área superior não-scrollável: hero, stats, título.
 *  Por padrão constrained (max-w-2xl + px-3).
 *  Use `fullBleed` para heroes que devem ir de borda a borda. */
function Context({ children, className, fullBleed }: ContextProps) {
  if (fullBleed) {
    return <div className={clsx('shrink-0', className)}>{children}</div>
  }
  return (
    <div className={clsx('shrink-0', CONTENT_MAX_W, CONTAINER_PADDING, className)}>
      {children}
    </div>
  )
}

/** Faixa não-scrollável visível durante o scroll: filtros, tabs, search. */
function Sticky({ children, className }: ScreenSectionProps) {
  return (
    <div className="relative z-[2] shrink-0">
      <div className={clsx(CONTENT_MAX_W, CONTAINER_PADDING, className)}>
        {children}
      </div>
    </div>
  )
}

/** Área scrollável: listas, timelines, conteúdo principal. */
function Content({ children, className, scrollBodyRef }: ContentProps) {
  const internalRef = useRef<HTMLDivElement>(null)
  const ref = scrollBodyRef ?? internalRef

  return (
    <div
      ref={ref}
      /* `layout-scroll-body` é marcador, não estilo: identifica o corpo de
         scroll para regras do shell (hoje, a folga da bottom nav flutuante). */
      className="layout-scroll-body min-h-0 flex-1 overflow-y-auto [-webkit-overflow-scrolling:touch] scrollbar-none [&::-webkit-scrollbar]:hidden"
    >
      <div className={clsx(CONTENT_MAX_W, CONTAINER_PADDING, className)}>
        {children}
      </div>
    </div>
  )
}

export const Screen = Object.assign(Root, { Context, Sticky, Content })
